// Auth context — wraps /api/auth/me. The single client-side identity source; mirrors the
// server's getCurrentUser() (ADR-008). Provides the dev role-picker, Admin impersonation, + logout.
import { createContext, useCallback, useContext, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { CurrentUser } from "@rrid/shared-types";
import { api, ApiError } from "./api.js";
import { persistLoginError, messageForLoginReason } from "./loginError.js";
import { qk } from "./queryKeys.js";

interface AuthValue {
  user: CurrentUser | null;
  /** Set when an Admin is viewing as another user. */
  impersonatedBy: string | null;
  /** True when the real principal (actor or self) is Admin. */
  canImpersonate: boolean;
  loading: boolean;
  devLogin: (email: string, snapshot?: string) => Promise<void>;
  /** @deprecated Prefer impersonate() — kept for login-screen AUTH_MODE=dev shortcut. */
  switchRole: (email: string) => Promise<void>;
  impersonate: (email: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
  logout: () => Promise<void>;
  completeEmailLink: (idToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return await api.get<CurrentUser>("/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          const reason =
            err.body && typeof err.body === "object" && "error" in err.body
              ? String((err.body as { error: unknown }).error)
              : "";
          const message = messageForLoginReason(reason);
          if (message) persistLoginError(message);
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const user = data ?? null;
  const impersonatedBy = user?.impersonatedBy ?? null;
  // Actor is always Admin when impersonating; otherwise check self.
  const canImpersonate = Boolean(impersonatedBy) || Boolean(user?.roles.includes("Admin"));

  const completeEmailLink = useCallback(
    async (idToken: string) => {
      await api.post("/auth/email-link", { idToken });
      qc.clear();
      await qc.invalidateQueries({ queryKey: qk.me });
      navigate("/", { replace: true });
    },
    [qc, navigate],
  );

  const refreshInPlace = async () => {
    // FR-L1: update the identity IN PLACE. `qc.clear()` would wipe the `me` query, drop App
    // into its full-screen loading remount, and reset the current portal to Home. Invalidating
    // (without clearing) refetches `me` + every mounted data query under the new role while the
    // current route stays put.
    await qc.invalidateQueries();
  };

  const value: AuthValue = {
    user,
    impersonatedBy,
    canImpersonate,
    loading: isLoading,
    devLogin: async (email, snapshot) => {
      await api.post("/auth/dev-login", { email, snapshot });
      qc.clear();
      await qc.invalidateQueries({ queryKey: qk.me });
      navigate("/", { replace: true });
    },
    switchRole: async (email) => {
      await api.post("/auth/dev-login", { email });
      await refreshInPlace();
    },
    impersonate: async (email) => {
      await api.post("/auth/impersonate", { email });
      await refreshInPlace();
    },
    stopImpersonating: async () => {
      await api.post("/auth/impersonate/stop");
      await refreshInPlace();
    },
    logout: async () => {
      await api.post("/auth/logout");
      qc.clear();
      await qc.invalidateQueries({ queryKey: qk.me });
      navigate("/login", { replace: true });
    },
    completeEmailLink,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
